import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { createEmbedChannel } from '../embed';
import { embedOrigin } from '../embed/flowMessages';
import { useFlowReporter } from '../embed/useFlowReporter';
import { ChatWidget } from '../flow/ChatWidget';
import { FlowRunner } from '../flow/FlowRunner';
import { EmptySession } from '../ui/EmptySession';

/**
 * The flow inside a partner's iframe: `/embed?token=<applicant token>&origin=<host origin>`.
 *
 * It runs the same flow as `/verify` and tells the host page what happens,
 * over the `trustsy:*` postMessage contract (`../embed`), pinned to `origin`:
 * `trustsy:ready` once the flow has loaded, `trustsy:state` on every step,
 * `trustsy:done` once (approved / rejected / manual_review), `trustsy:error`
 * when the flow cannot load or fails. The messages carry no applicant data;
 * the partner's webhook stays the authoritative record.
 *
 * The redirect return leg is off here: the host page is already where the
 * applicant should be.
 */
export function EmbedScreen() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token')?.trim() || null;
  const origin = embedOrigin(params.get('origin'));

  const channel = useMemo(
    () => (origin && window.parent !== window ? createEmbedChannel(window.parent, origin) : null),
    [origin],
  );
  useEffect(() => () => channel?.close(), [channel]);

  const { onSnapshot, onLoadError } = useFlowReporter(channel ? channel.send : null);

  if (!token) return <EmptySession kind="missing" />;
  if (!origin) {
    // For the integrator: the applicant sees the plain message below.
    console.error(
      'trustsy embed: ?origin= must be the embedding page origin, e.g. https://shop.example',
    );
    return (
      <main className="screen">
        <div className="card enter">
          <h1 className="step-title">{t('embed.originMissing.title')}</h1>
          <p className="step-text">{t('embed.originMissing.body')}</p>
        </div>
      </main>
    );
  }

  return (
    <>
      <FlowRunner
        onLoadError={onLoadError}
        onSnapshot={onSnapshot}
        returnLeg={false}
        token={token}
        withHandoff
      />
      <ChatWidget token={token} />
    </>
  );
}
